import { graphql, useMutation } from "react-relay";
import { AHButton, AHIcon, AHText, Box, Input, ListItem } from "arkh";
import { useCallback, useLayoutEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { ServiceAccountCreateScreenMutation } from "./__generated__/ServiceAccountCreateScreenMutation.graphql";
import { StoreAdminNavigationScreenProps } from "../StoreAdmin/StoreAdminNavigation";
import { HeaderButton } from "../../components";
import * as Clipboard from "expo-clipboard";
import { Modal, Pressable, StyleSheet, View } from "react-native";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import {
  ListSectionHeader,
  ListSectionHeaderTitle,
} from "@/src/components/atoms/ListSectionHeader";

export const ServiceAccountCreateScreen = (
  props: StoreAdminNavigationScreenProps<"ServiceAccountCreate">
) => {
  const [createServiceAccount, loading] =
    useMutation<ServiceAccountCreateScreenMutation>(graphql`
      mutation ServiceAccountCreateScreenMutation(
        $name: String!
        $tokens: [ServiceAccountAuthTokenInput!]
      ) {
        store {
          createServiceAccount(name: $name, tokens: $tokens) {
            id
            authTokens {
              token
            }
          }
        }
      }
    `);

  const [name, setName] = useState("");
  const [token, setToken] = useState<string | null>(null);

  const onCreate = useCallback(() => {
    createServiceAccount({
      variables: {
        name,
        tokens: [{ expiresAt: null }],
      },
      onCompleted: (data) => {
        const token =
          data.store.createServiceAccount.authTokens?.[0]?.token ?? "";

        setToken(token);
      },
    });
  }, [createServiceAccount, name, setToken]);

  useLayoutEffect(() => {
    props.navigation.setOptions({
      headerRight: () => (
        <HeaderButton
          nativeHeader
          type="CREATE"
          onPress={onCreate}
          disabled={loading}
        />
      ),
    });
  }, [props.navigation, onCreate, loading]);

  return (
    <>
      <Box space="medium">
        <ListSectionHeader>
          <ListSectionHeaderTitle>
            <FormattedMessage
              description="ServiceAccountCreateScreen - name - title"
              defaultMessage="Name"
            />
          </ListSectionHeaderTitle>
        </ListSectionHeader>
        <ListItem isFirst isLast>
          <Input
            placeholder="Name"
            value={name}
            onChangeText={setName}
            borderColor="transparent"
            space="small"
          />
        </ListItem>
      </Box>
      {token !== null && (
        <TokenCreatedModal
          token={token}
          onClose={() => props.navigation.pop()}
        />
      )}
    </>
  );
};

const TokenCreatedModal = (props: { token: string; onClose: () => void }) => {
  const intl_tokens = useIntlTokens();

  return (
    <Modal
      visible
      transparent
      presentationStyle="overFullScreen"
      animationType="fade"
      style={{ flex: 1 }}
    >
      <Box
        style={[
          StyleSheet.absoluteFill,
          { justifyContent: "center", alignItems: "center" },
        ]}
      >
        <Pressable style={StyleSheet.absoluteFill} onPress={props.onClose}>
          <View
            style={[
              StyleSheet.absoluteFill,
              {
                backdropFilter: "blur(1.6px)",
                background: "rgba(0,0,0,0.1)",
                backgroundColor: "rgba(0,0,0,0.1)",
              } as any,
            ]}
          />
        </Pressable>
        <Box
          spaceHorizontal="medium"
          style={{ minHeight: 300, width: "100%", maxWidth: 500 }}
        >
          <Box
            color="surface-primary"
            cornerRadius="large"
            space="large"
            gap="medium"
            style={{ flex: 1, overflow: "hidden" }}
          >
            <AHText type="title">
              <FormattedMessage
                description="ServiceAccountCreateScreen - key created - title"
                defaultMessage="External Service Key Created"
              />
            </AHText>
            <AHText type="body" color="text-primary">
              <FormattedMessage
                description="ServiceAccountCreateScreen - key created - description"
                defaultMessage={
                  "This key is only shown once. Copy and share only with trusted providers."
                }
              />
            </AHText>
            <Box
              cornerRadius="medium"
              borderColor="border-cta"
              space="medium"
              style={{
                flexDirection: "row",
                alignItems: "center",
                borderWidth: 1,
              }}
            >
              <Box
                style={[StyleSheet.absoluteFill, { opacity: 0.1 }]}
                color="surface-cta"
              />
              <Box style={{ flex: 1 }}>
                <AHText type="detail" size="body" numberOfLines={1}>
                  {props.token}
                </AHText>
              </Box>
              <Pressable
                style={({ pressed }) => ({ opacity: pressed ? 0.7 : 1 })}
                onPress={() => {
                  Clipboard.setStringAsync(props.token);
                }}
              >
                <Box gap="small" style={{ flexDirection: "row" }}>
                  <AHText color="text-cta" spaceLeft="small">
                    <FormattedMessage
                      description="ServiceAccountCreateScreen - key created - copy label"
                      defaultMessage="Copy"
                    />
                  </AHText>
                  <AHIcon type="duplicate" color="text-cta" />
                </Box>
              </Pressable>
            </Box>
            <Box
              style={{
                flex: 1,
                flexDirection: "row",
                justifyContent: "flex-end",
                alignItems: "flex-end",
              }}
            >
              <AHButton title={intl_tokens.Close} onPress={props.onClose} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};
